/**
 * i18n Configuration
 * 
 * Internationalization setup using react-i18next
 * Supports: English (en), Norwegian Bokmål (nb)
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from './locales/en/common.json';
import enWorkspace from './locales/en/workspace.json';
import enCases from './locales/en/cases.json';
import enNav from './locales/en/nav.json';
import enErrors from './locales/en/errors.json';
import enSettings from './locales/en/settings.json';
import enInbox from './locales/en/inbox.json';
import enDrafting from './locales/en/drafting.json';
import enDashboard from './locales/en/dashboard.json';
import enTriage from './locales/en/triage.json';
import enCaseQueue from './locales/en/caseQueue.json';
import enCategory from './locales/en/category.json';
import enOutlook from './locales/en/outlook.json';
import enChat from './locales/en/chat.json';
import enTimeTracking from './locales/en/timeTracking.json';
import enInvoicing from './locales/en/invoicing.json';
import enCaseWorkspace from './locales/en/caseWorkspace.json';
import enTeam from './locales/en/team.json';
import enKyc from './locales/en/kyc.json';

import nbCommon from './locales/nb/common.json';
import nbWorkspace from './locales/nb/workspace.json';
import nbCases from './locales/nb/cases.json';
import nbNav from './locales/nb/nav.json';
import nbErrors from './locales/nb/errors.json';
import nbSettings from './locales/nb/settings.json';
import nbInbox from './locales/nb/inbox.json';
import nbDrafting from './locales/nb/drafting.json';
import nbDashboard from './locales/nb/dashboard.json';
import nbTriage from './locales/nb/triage.json';
import nbCaseQueue from './locales/nb/caseQueue.json';
import nbCategory from './locales/nb/category.json';
import nbOutlook from './locales/nb/outlook.json';
import nbChat from './locales/nb/chat.json';
import nbTimeTracking from './locales/nb/timeTracking.json';
import nbInvoicing from './locales/nb/invoicing.json';
import nbCaseWorkspace from './locales/nb/caseWorkspace.json';
import nbTeam from './locales/nb/team.json';
import nbKyc from './locales/nb/kyc.json';

const resources = {
  en: {
    common: enCommon,
    workspace: enWorkspace,
    cases: enCases,
    nav: enNav,
    errors: enErrors,
    settings: enSettings,
    inbox: enInbox,
    drafting: enDrafting,
    dashboard: enDashboard,
    triage: enTriage,
    caseQueue: enCaseQueue,
    category: enCategory,
    outlook: enOutlook,
    chat: enChat,
    timeTracking: enTimeTracking,
    invoicing: enInvoicing,
    caseWorkspace: enCaseWorkspace,
    team: enTeam,
    kyc: enKyc
  },
  nb: {
    common: nbCommon,
    workspace: nbWorkspace,
    cases: nbCases,
    nav: nbNav,
    errors: nbErrors,
    settings: nbSettings,
    inbox: nbInbox,
    drafting: nbDrafting,
    dashboard: nbDashboard,
    triage: nbTriage,
    caseQueue: nbCaseQueue,
    category: nbCategory,
    outlook: nbOutlook,
    chat: nbChat,
    timeTracking: nbTimeTracking,
    invoicing: nbInvoicing,
    caseWorkspace: nbCaseWorkspace,
    team: nbTeam,
    kyc: nbKyc
  }
};

// Get saved language from localStorage
const getSavedLanguage = (): string => {
  const lexyLang = localStorage.getItem('lexy_language');
  const fallbackLang = localStorage.getItem('language');
  const lang = lexyLang || fallbackLang;
  // Map old 'no' code to 'nb'
  if (lang === 'no') return 'nb';
  return lang || 'nb'; // Default to Norwegian
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: getSavedLanguage(), // Explicitly set initial language from localStorage
    fallbackLng: 'nb', // Default to Norwegian
    defaultNS: 'common',
    
    // Language detection options
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'lexy_language',
      caches: ['localStorage']
    },
    
    interpolation: {
      escapeValue: false // React already escapes
    },
    
    // React options
    react: {
      useSuspense: false
    }
  });

export default i18n;
